import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Kontakt() {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    track: "tenant",
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Bitte stimmen Sie den Datenschutzbedingungen zu.");
    if (!form.name || !form.email || !form.message) return toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank – Ihre Nachricht ist bei uns eingegangen.");
      setForm({ track: form.track, name: "", email: "", phone: "", message: "", consent: false });
    } catch {
      toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderTrack = (id, heading, email, phone, value) => (
    <button
      type="button"
      onClick={() => setForm((f) => ({ ...f, track: value }))}
      data-testid={id}
      className={`text-left p-6 md:p-8 border transition-colors duration-200 ${
        form.track === value
          ? "border-primary bg-background"
          : "border-border bg-background hover:border-foreground/40"
      }`}
    >
      <div className="text-xs uppercase tracking-[0.25em] text-primary mb-3">{heading}</div>
      <div className="space-y-2 text-sm">
        <a href={`mailto:${email}`} className="flex items-center gap-2 text-foreground hover:text-primary">
          <Mail className="h-4 w-4" strokeWidth={1.5} /> {email}
        </a>
        <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 text-foreground hover:text-primary">
          <Phone className="h-4 w-4" strokeWidth={1.5} /> {phone}
        </a>
      </div>
      <div className="mt-5 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
        {form.track === value ? "ausgewählt" : "Auswählen"}
      </div>
    </button>
  );

  return (
    <div data-testid="page-kontakt">
      <section className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-12">
        <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary mb-6">
          <span className="inline-block w-2 h-2 bg-accent" />
          Kontakt
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.05] max-w-4xl">
          Schreiben Sie <em className="italic">uns</em>.
        </h1>
        <p className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
          Wählen Sie den passenden Kontaktweg – oder nutzen Sie das Formular.
          Wir antworten persönlich, in der Regel innerhalb weniger Werktage.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderTrack("contact-track-tenants", "Für Mieter & Mietinteressenten", "post@keppel-immobilien.de", "0221 – 64 00 90 85", "tenant")}
          {renderTrack("contact-track-partners", "Für Makler, Servicepartner, Handwerker", "objekte@keppel-immobilien.de", "0221 – 64 00 90 81", "partner")}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-32">
        <form onSubmit={submit} className="max-w-2xl space-y-6" data-testid="contact-form">
          <div className="text-xs uppercase tracking-[0.25em] text-primary border-b border-border pb-3 mb-2">
            Ihre Nachricht
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Ihr Name *</Label>
            <Input
              data-testid="contact-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Vor- und Nachname"
              className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
              required
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">E-Mail *</Label>
              <Input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ihre@email.de"
                className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Telefon</Label>
              <Input
                data-testid="contact-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="optional"
                className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-border h-12 px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Ihre Nachricht *</Label>
            <Textarea
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={6}
              placeholder="Worum geht es?"
              className="rounded-none bg-transparent border border-border focus-visible:ring-0 focus-visible:border-primary text-base"
              required
            />
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="contact-consent"
              data-testid="contact-consent"
              checked={form.consent}
              onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
              className="rounded-none border-foreground/40 data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-1"
            />
            <Label htmlFor="contact-consent" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">
              Ich stimme der Verarbeitung meiner Daten gemäß{" "}
              <a href="/datenschutz" className="text-primary underline underline-offset-4">Datenschutzerklärung</a> zu.
            </Label>
          </div>

          <Button
            type="submit"
            data-testid="contact-submit"
            disabled={submitting}
            className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-10 text-sm tracking-wide w-full sm:w-auto"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Nachricht senden
          </Button>
        </form>
      </section>
    </div>
  );
}
